import React, { useState, useEffect } from "react";
import { GoPin } from "react-icons/go";
import Draggable from "react-draggable";
import { useNavigate } from "react-router-dom";
import dust from "../../assets/dust-particles.png";
import PinboardPopup from "./PinboardPopup";
import { useDispatch, useSelector } from "react-redux";
import { getAllPinboard } from "../../redux/ApiSlice/Neighborhood/NeighborhoodPinboardSlice";
import { AiOutlineRight, AiOutlineLeft } from 'react-icons/ai'

const Pinboard = ({ setSelectedPin, selectedPin, setIsPopupOpen, isPopupOpen }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [positions, setPositions] = useState(null);
    // const [isPopupOpen, setIsPopupOpen] = useState(false);
    // const [selectedPin, setSelectedPin] = useState(null);
    const [pinbordData, setPinboardData] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [draggedPinId, setDraggedPinId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const { pinboardData, loading, next, previous, count } = useSelector((state) => state.NeighborhoodPinboard);


    // Fetch data on initial load
    // useEffect(() => {
    //     dispatch(getAllPinboard());
    // }, [dispatch]);

    // // Fetch latest data when popup is closed
    useEffect(() => {
        if (!isPopupOpen) {
            dispatch(getAllPinboard({ page: currentPage }));
        }
    }, [isPopupOpen, dispatch, currentPage]);

    useEffect(() => {
        const savedPositions = JSON.parse(localStorage.getItem("pinboardPositions")) || {};
        setPositions(savedPositions);
    }, []);

    // Initialize positions for new pins
    useEffect(() => {
        if (!pinboardData || !Array.isArray(pinboardData) || positions === null) return;

        const PIN_WIDTH = 110;
        const PIN_HEIGHT = 68;
        const MARGIN = 10;
        let hasNewPins = false;
        const updatedPositions = { ...positions };

        pinboardData.forEach((pin) => {
            if (!updatedPositions[pin.id]) {
                // Generate initial position for new pin
                const newPosition = generateInitialPosition(updatedPositions, PIN_WIDTH, PIN_HEIGHT, MARGIN);
                updatedPositions[pin.id] = newPosition;
                hasNewPins = true;
            }
        });

        if (hasNewPins) {
            setPositions(updatedPositions);
            localStorage.setItem("pinboardPositions", JSON.stringify(updatedPositions));
        }
    }, [pinboardData, positions]);

    // Helper function to generate initial position for new pins
    const generateInitialPosition = (existingPositions, pinWidth, pinHeight, margin) => {
        const GRID_SIZE = 120; // Space between pins in grid
        const START_X = 20;
        const START_Y = 20;
        const MAX_COLS = 10; // Max pins per row before wrapping

        // Try to place in a grid pattern
        let row = 0;
        let col = 0;

        while (true) {
            const testX = START_X + (col * GRID_SIZE);
            const testY = START_Y + (row * GRID_SIZE);

            // Check if this position overlaps with any existing pin
            const isOverlapping = Object.values(existingPositions).some(pos => {
                return Math.abs(pos.x - testX) < pinWidth + margin &&
                    Math.abs(pos.y - testY) < pinHeight + margin;
            });

            if (!isOverlapping) {
                return { x: testX, y: testY };
            }

            // Move to next grid position
            col++;
            if (col >= MAX_COLS) {
                col = 0;
                row++;
            }

            // Safety check to prevent infinite loop
            if (row > 50) {
                // Return a random position as fallback
                return {
                    x: Math.random() * 500 + 20,
                    y: Math.random() * 500 + 20
                };
            }
        }
    };

    // Cleanup auto-scroll listener on unmount
    useEffect(() => {
        return () => {
            // Cleanup if needed
        };
    }, []);

    // Auto-scroll functionality - only when dragging a pin
    const handleAutoScroll = (e) => {
        const container = document.querySelector('.pinboard-container');
        if (!container || !isDragging) return;

        const containerRect = container.getBoundingClientRect();
        const scrollZone = 40; // Pixels from edge to trigger scroll
        const scrollSpeed = 6; // Pixels per scroll step

        let scrollX = 0;
        let scrollY = 0;

        // Get current mouse position
        const mouseX = e.clientX || e.pageX || 0;
        const mouseY = e.clientY || e.pageY || 0;

        // Check if dragged pin is near container edges
        if (mouseX - containerRect.left < scrollZone && container.scrollLeft > 0) {
            scrollX = -scrollSpeed; // Scroll left
        } else if (containerRect.right - mouseX < scrollZone) {
            scrollX = scrollSpeed; // Scroll right
        }

        if (mouseY - containerRect.top < scrollZone && container.scrollTop > 0) {
            scrollY = -scrollSpeed; // Scroll up
        } else if (containerRect.bottom - mouseY < scrollZone) {
            scrollY = scrollSpeed; // Scroll down
        }

        // Perform scroll only when pin is near edges
        if (scrollX !== 0 || scrollY !== 0) {
            container.scrollBy(scrollX, scrollY);
        }
    };

    const handleStart = (e, data, id) => {
        setDraggedPinId(id);
        setIsDragging(true);
    };

    const handleDrag = (e, data, id) => {
        // Call auto-scroll during drag
        handleAutoScroll(e);
    };

    const handleStop = (e, data, id) => {
        const newPosition = { x: data.x, y: data.y };

        // Check for collisions with other pins and find valid position
        const validPosition = findValidPosition(newPosition, id);

        const newPositions = { ...positions, [id]: validPosition };
        setPositions(newPositions);
        localStorage.setItem("pinboardPositions", JSON.stringify(newPositions));
        setDraggedPinId(null); // Reset dragged pin
        setIsDragging(false);
    };

    // More robust collision detection and position finding
    const findValidPosition = (desiredPosition, currentId) => {
        const PIN_WIDTH = 110;
        const PIN_HEIGHT = 68;
        const MARGIN = 1; // Minimal margin - almost touching

        // Get container dimensions - including full scroll area
        const container = document.querySelector('.pinboard-container');
        const innerContainer = container?.querySelector('div');
        if (!container || !innerContainer) return desiredPosition;

        // Use the inner container's full dimensions for dragging bounds
        const containerStyle = window.getComputedStyle(innerContainer);
        const maxX = Math.max(0, parseInt(containerStyle.width) - PIN_WIDTH - 20);
        const maxY = Math.max(0, parseInt(containerStyle.height) - PIN_HEIGHT - 20);

        // Helper function to check if position overlaps with any existing pin
        const isPositionValid = (x, y) => {
            // Check bounds for full scroll area
            if (x < 0 || y < 0 || x > maxX || y > maxY) {
                return false;
            }

            const testPin = {
                left: x,
                right: x + PIN_WIDTH,
                top: y,
                bottom: y + PIN_HEIGHT
            };

            // Check collision with all other pins
            for (const [pinId, position] of Object.entries(positions)) {
                if (pinId === currentId.toString()) continue;

                const existingPin = {
                    left: position.x,
                    right: position.x + PIN_WIDTH,
                    top: position.y,
                    bottom: position.y + PIN_HEIGHT
                };

                // Check if pins overlap (with margin)
                if (testPin.left < existingPin.right + MARGIN &&
                    testPin.right > existingPin.left - MARGIN &&
                    testPin.top < existingPin.bottom + MARGIN &&
                    testPin.bottom > existingPin.top - MARGIN) {
                    return false; // Overlap detected
                }
            }
            return true; // No overlap
        };

        // First, try the desired position
        if (isPositionValid(desiredPosition.x, desiredPosition.y)) {
            return desiredPosition;
        }

        // If desired position is invalid, search for nearby valid positions
        const searchRadius = 60; // Increased search radius for larger area
        const step = 2; // Small step size for precise positioning

        for (let radius = step; radius <= searchRadius; radius += step) {
            // Search in a spiral pattern around the desired position
            for (let angle = 0; angle < 360; angle += 30) {
                const radian = (angle * Math.PI) / 180;
                const testX = desiredPosition.x + Math.cos(radian) * radius;
                const testY = desiredPosition.y + Math.sin(radian) * radius;

                if (isPositionValid(testX, testY)) {
                    return { x: testX, y: testY };
                }
            }
        }

        // If no valid position found nearby, find any available position
        for (let y = 0; y <= maxY; y += step * 5) { // Larger steps for efficiency
            for (let x = 0; x <= maxX; x += step * 5) {
                if (isPositionValid(x, y)) {
                    return { x, y };
                }
            }
        }

        // Last resort: return bounded position
        return {
            x: Math.max(0, Math.min(desiredPosition.x, maxX)),
            y: Math.max(0, Math.min(desiredPosition.y, maxY))
        };
    };

    const handlePinClick = (pin) => {
        const latestPin = pinboardData?.find(item => item.id === pin.id);
        setSelectedPin(latestPin || pin);
        setIsPopupOpen(true);
    };

    const closePopup = () => {
        setIsPopupOpen(false);
        setSelectedPin(null);
    };

    const handleNextPage = () => {
        if (next) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (previous) {
            setCurrentPage(prev => prev - 1);
        }
    };

    if (positions === null) return null;

    return (
        <div className="px-2 pb-2 relative">
            <p className="font-bold text-[18px] text-center">Pinboard</p>
            <div
                className="pinboard-container w-full overflow-x-scroll overflow-y-scroll h-[40vh] bg-[#635151] border-[10px] border-[#2e2222] rounded-lg relative"
                style={{
                    backgroundSize: "contain",
                    backgroundRepeat: "repeat",
                    backgroundImage: `url(${dust})`,
                }}
            >
                {/* Create a much larger scrollable area for dragging */}
                <div className="relative min-w-[200vw] min-h-[200vh] w-[300%] h-[300%]">
                    {Array.isArray(pinboardData) && pinboardData?.map((data) => (
                        <Draggable
                            key={data.id}
                            position={positions[data.id] || { x: 0, y: 0 }}
                            onStart={(e, dragData) => handleStart(e, dragData, data.id)}
                            onDrag={(e, dragData) => handleDrag(e, dragData, data.id)}
                            onStop={(e, dragData) => handleStop(e, dragData, data.id)}
                            bounds="parent"
                        >
                            <div
                                className={`bg-white text-black hover:bg-[#49bfd4] hover:text-white w-[110px] h-[68px] overflow-y-scroll absolute rounded-sm cursor-pointer select-none transition-all duration-200 ${draggedPinId === data.id ? 'transform scale-105 shadow-lg ring-2 ring-blue-400 z-50' : 'z-10'
                                    }`}
                                onClick={() => handlePinClick(data)}
                                style={{
                                    top: 0,
                                    left: 0,
                                    boxShadow: draggedPinId === data.id
                                        ? '0 8px 16px rgba(0,0,0,0.3)'
                                        : '0 2px 4px rgba(0,0,0,0.2)'
                                }}
                            >
                                <GoPin className="sticky top-0 text-[12px] text-[#FF9900] bg-black p-[2px]" />
                                <p className="text-[10px] text-center p-1">{data.title}</p>
                            </div>
                        </Draggable>
                    ))}
                </div>
            </div>
            <div className="flex justify-end items-center w-full">
                <button
                    onClick={handlePreviousPage}
                    disabled={!previous}
                    className="bg-[#BDE6FF] hover:bg-[#7FCFFF] text-black py-2 px-4 rounded-lg mt-2 mr-2 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <AiOutlineLeft size={16} />
                </button>
                <button
                    onClick={handleNextPage}
                    disabled={!next}
                    className="bg-[#BDE6FF] hover:bg-[#7FCFFF] text-black py-2 px-4 rounded-lg mt-2 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <AiOutlineRight size={16} />
                </button>
            </div>

            <div className="flex justify-center items-center w-full">
                <button
                    onClick={() => navigate("/neighborhood/pinboard/create")}
                    className="w-[80%] bg-[#3d3d3b] hover:bg-[#252524] text-white py-2 rounded-lg mt-[2rem] transition-colors duration-300"
                >
                    Post To Pinboard
                </button>
            </div>
        </div>
    );
};

export default Pinboard;